import AppError from '@/components/AppError';
import { TappPageHeader } from '@/components/ui/TappPageHeader';
import { TappPill, TappPillProps } from '@/components/ui/TappPill';
import { cn } from '@/components/ui/utils';
import { Ride, RideStatus } from '@/lib/models/ride';
import { ApiUtils } from '@/lib/utils/api';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { TappLink } from '@/components/ui/TappLink';

const RideDetailsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const {
    data: ride,
    isLoading,
    error,
  } = useQuery({
    queryKey: [`ride/${id}`],
    queryFn: () => ApiUtils.get<{ data: Ride }>(`rides/${id}`).then((res) => res.data),
  });

  const normalizeAnonymizedValue = (value: string) => value.split('_')[2];

  if (error) return <AppError error={error} />;

  const statusColors: Record<RideStatus, TappPillProps['color']> = {
    [RideStatus.inProgress]: 'primary',
    [RideStatus.driverArrived]: 'primary',
    [RideStatus.requested]: 'primary',
    [RideStatus.awaitingCheckout]: 'primary',
    [RideStatus.accepted]: 'primary',
    [RideStatus.completed]: 'success',
    [RideStatus.cancelled]: 'destructive',
  };

  return (
    <>
      <TappPageHeader title={t('rideDetails')} defaultBack backLink="/admin/rides" />
      <div className={cn('mx-auto max-w-4xl space-y-6', { isLoading })}>
        {ride && (
          <>
            {/* Status and Basic Info */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold">{t('rideInformation')}</h2>
                <TappPill color={statusColors[ride.status]}>{t(ride.status)}</TappPill>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
                  <p className="font-medium">{dayjs(ride.createdAt).format('DD.MM.YYYY HH:mm')}</p>
                </div>

                {(ride.pricing?.finalFare || ride.price) && (
                  <div>
                    <p className="text-sm text-muted-foreground">{t('price')}</p>
                    <p className="font-medium">
                      {(ride.pricing?.finalFare || ride.price)?.toFixed(2)} {ride.pricing?.currency || 'RON'}
                    </p>
                  </div>
                )}

                {(ride.route?.estimatedDistance || ride.distance) && (
                  <div>
                    <p className="text-sm text-muted-foreground">{t('distance')}</p>
                    <p className="font-medium">
                      {((ride.route?.estimatedDistance || ride.distance || 0) / 1000).toFixed(2)} km
                    </p>
                  </div>
                )}

                {(ride.route?.actualDuration || ride.route?.estimatedDuration) && (
                  <div>
                    <p className="text-sm text-muted-foreground">{t('duration')}</p>
                    <p className="font-medium">
                      {Math.round((ride.route?.actualDuration || ride.route?.estimatedDuration || 0) / 60)}{' '}
                      {t('minutes')}
                    </p>
                  </div>
                )}

                {ride.paymentMethod && (
                  <div>
                    <p className="text-sm text-muted-foreground">{t('paymentMethod')}</p>
                    <p className="font-medium">{t(ride.paymentMethod)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Customer and Driver Info */}
            <div className="rounded-lg border border-border bg-card p-6">
              <h2 className="mb-4 text-xl font-semibold">{t('participants')}</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">{t('customer')}</p>
                  {ride.customerId ? (
                    <div>
                      <TappLink to={`/admin/customers/${ride.customerId}`} className="font-medium hover:underline">
                        {ride.customerFirstName
                          ? `${ride.customerFirstName} ${ride.customerLastName}`
                          : ride.customerName || ride.customerId}
                      </TappLink>
                      {ride.customerPhoneNumber && (
                        <p className="text-sm text-muted-foreground">
                          {ride.customerStatus === 'deleted'
                            ? normalizeAnonymizedValue(ride.customerPhoneNumber)
                            : ride.customerPhoneNumber}
                        </p>
                      )}
                      {ride.customerRating && (
                        <p className="text-sm text-muted-foreground">
                          {t('ratingReceived')}: {ride.customerRating}/5
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="font-medium">
                      {ride.customerFirstName
                        ? `${ride.customerFirstName} ${ride.customerLastName}`
                        : ride.customerName || '-'}
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">{t('driver')}</p>
                  {ride.driverId && (ride.driverFirstName || ride.driverName) ? (
                    <div>
                      <TappLink to={`/admin/drivers/${ride.driverId}`} className="font-medium hover:underline">
                        {ride.driverFirstName ? `${ride.driverFirstName} ${ride.driverLastName}` : ride.driverName}
                      </TappLink>
                      {ride.driverPhoneNumber && (
                        <p className="text-sm text-muted-foreground">
                          {ride.driverStatus === 'deleted'
                            ? normalizeAnonymizedValue(ride.driverPhoneNumber)
                            : ride.driverPhoneNumber}
                        </p>
                      )}
                      {ride.driverRating && (
                        <p className="text-sm text-muted-foreground">
                          {t('ratingReceived')}: {ride.driverRating}/5
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="font-medium">-</p>
                  )}
                </div>
              </div>
            </div>

            {/* Locations */}
            <div className="rounded-lg border border-border bg-card p-6">
              <h2 className="mb-4 text-xl font-semibold">{t('locations')}</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t('pickup')}</p>
                  <p className="font-medium">{ride.pickup?.address || ride.pickupAddress}</p>
                </div>

                <div className="border-t border-border pt-4">
                  <p className="text-sm text-muted-foreground">{t('destination')}</p>
                  <p className="font-medium">{ride.destination?.address || ride.destinationAddress}</p>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="rounded-lg border border-border bg-card p-6">
              <h2 className="mb-4 text-xl font-semibold">{t('timeline')}</h2>
              <div className="space-y-3">
                {(ride.timeline?.requestedAt || ride.createdAt) && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('requested')}</span>
                    <span className="font-medium">
                      {dayjs(ride.timeline?.requestedAt || ride.createdAt).format('DD.MM.YYYY HH:mm')}
                    </span>
                  </div>
                )}

                {ride.timeline?.acceptedAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('accepted')}</span>
                    <span className="font-medium">{dayjs(ride.timeline.acceptedAt).format('DD.MM.YYYY HH:mm')}</span>
                  </div>
                )}

                {(ride.timeline?.pickedUpAt || ride.startedAt) && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('pickedUp')}</span>
                    <span className="font-medium">
                      {dayjs(ride.timeline?.pickedUpAt || ride.startedAt).format('DD.MM.YYYY HH:mm')}
                    </span>
                  </div>
                )}

                {(ride.timeline?.completedAt || ride.completedAt) && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('completed')}</span>
                    <span className="font-medium">
                      {dayjs(ride.timeline?.completedAt || ride.completedAt).format('DD.MM.YYYY HH:mm')}
                    </span>
                  </div>
                )}

                {(ride.timeline?.cancelledAt || ride.cancelledAt) && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('cancelled')}</span>
                    <span className="font-medium">
                      {dayjs(ride.timeline?.cancelledAt || ride.cancelledAt).format('DD.MM.YYYY HH:mm')}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between border-t border-border pt-3">
                  <span className="text-muted-foreground">{t('lastUpdated')}</span>
                  <span className="font-medium">{dayjs(ride.updatedAt).format('DD.MM.YYYY HH:mm')}</span>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            {(ride.specialInstructions || ride.luggage !== undefined || ride.pet !== undefined) && (
              <div className="rounded-lg border border-border bg-card p-6">
                <h2 className="mb-4 text-xl font-semibold">{t('additionalInformation')}</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {ride.specialInstructions && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-muted-foreground">{t('specialInstructions')}</p>
                      <p className="font-medium">{ride.specialInstructions}</p>
                    </div>
                  )}

                  {ride.luggage !== undefined && (
                    <div>
                      <p className="text-sm text-muted-foreground">{t('luggage')}</p>
                      <p className="font-medium">{ride.luggage ? t('yes') : t('no')}</p>
                    </div>
                  )}

                  {ride.pet !== undefined && (
                    <div>
                      <p className="text-sm text-muted-foreground">{t('pet')}</p>
                      <p className="font-medium">{ride.pet ? t('yes') : t('no')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default RideDetailsPage;
